import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import type UserService from 'walden-fast/services/user';
import type FastApiService from 'walden-fast/services/fast-api';
import FormValidationService from 'walden-fast/services/form-validation';
import type { User } from 'walden-fast/types/user';
import {
  isContactPostError,
  type ContactPostError,
} from 'walden-fast/types/fast-backend/contact';
import type { ContactSubmissionRequest } from 'walden-fast/types/fast-backend/contact';

type FieldName = keyof ContactSubmissionRequest;
type ValidationErrors = Partial<Record<FieldName, string[]>>;

function normalizeContactErrors(error: ContactPostError): ValidationErrors {
  const { errors } = error.payload;
  const keyMap: Record<string, FieldName> = {
    Topic: 'topic',
    Subject: 'subject',
    TicketType: 'ticketType',
    Description: 'description',
  };

  const normalized: ValidationErrors = {};
  for (const [apiKey, messages] of Object.entries(errors)) {
    if (!messages || messages.length === 0) continue;
    const field = keyMap[apiKey] ?? (apiKey as FieldName);
    normalized[field] = messages.map(String);
  }
  return normalized;
}

export default class AppContactSubmitTicketController extends Controller {
  @service('user') declare userService: UserService;
  @service('intl') declare intlService: IntlService;
  @service('fast-api') declare fastApiService: FastApiService;
  @service('form-validation')
  declare formValidationService: FormValidationService;

  declare model: { user: User };

  @tracked clientNumber = '';
  @tracked _email = '';
  @tracked subject = '';
  @tracked description = '';
  @tracked topic = '';
  @tracked ticketType = 1;

  @tracked private formChangeVersion = 0;
  @tracked private lastSuccessfulSubmitVersion: number | null = null;

  private bumpFormVersion(): void {
    this.formChangeVersion++;
  }

  get hasValidationErrors(): boolean {
    return this.formValidationService.hasValidationErrors();
  }

  get isFormPristine(): boolean {
    return (
      this.subject === '' &&
      this.description === '' &&
      this.topic === '' &&
      this.ticketType === 1 // keep in sync with the default
    );
  }

  get sentSuccessfully(): boolean {
    return (
      this.lastSuccessfulSubmitVersion !== null &&
      this.lastSuccessfulSubmitVersion === this.formChangeVersion &&
      Boolean(this.submitTicket.lastSuccessful) &&
      !this.hasValidationErrors &&
      !this.isFormPristine
    );
  }

  get sendFailedNonValidation(): boolean {
    const lastError = this.submitTicket.lastErrored?.error;
    return Boolean(lastError && !isContactPostError(lastError));
  }

  private clearFieldError(field: FieldName): void {
    this.formValidationService.clearFieldError(field);
    this.bumpFormVersion();
  }

  submitTicket = task(async (event: Event) => {
    event?.preventDefault?.();
    this.formValidationService.clearAllErrors();

    const submitVersion = this.formChangeVersion;

    const body: ContactSubmissionRequest = {
      subject: this.subject,
      description: this.description,
      topic: this.topic,
      ticketType: this.ticketType,
    };

    try {
      const result = await this.fastApiService.post('/support', body);

      this.lastSuccessfulSubmitVersion = submitVersion;

      return result;
    } catch (error: unknown) {
      if (isContactPostError(error)) {
        this.formValidationService.setValidationErrors(
          normalizeContactErrors(error),
        );
        return;
      }
      throw error;
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChangeTopic = (selected: unknown, _event: Event): void => {
    this.topic = selected as string;
    this.clearFieldError('topic');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChangeTicketType = (selected: unknown, _event: Event): void => {
    this.ticketType = parseInt(selected as string, 10);
    this.clearFieldError('ticketType');
  };

  @action onChangeSubject(value: string): void {
    this.subject = value;
    this.clearFieldError('subject');
  }

  @action onChangeDescription(value: string): void {
    this.description = value;
    this.clearFieldError('description');
  }

  @action submitAnother(): void {
    this.reset();
  }

  reset(): void {
    this.description = '';
    this.topic = '';
    this.subject = '';
    this.ticketType = 1;
    this.formValidationService.clearAllErrors();
    this.lastSuccessfulSubmitVersion = null;
    this.bumpFormVersion();
  }
}
